import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Radio,
  Chip,
  Alert,
  Button,
  Collapse,
  IconButton,
  LinearProgress,
  Card,
  CardContent,
  Grid,
  Divider,
  FormControl,
  Select,
  MenuItem,
  Checkbox
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Person as PersonIcon,
  ContentCopy as DuplicateIcon,
  Merge as MergeIcon,
  Add as AddIcon,
  SkipNext as SkipIcon,
  SelectAll as SelectAllIcon
} from '@mui/icons-material';

// eslint-disable-next-line max-lines-per-function
function DuplicateResolver({ duplicates, resolutions, onResolutionChange, csvData, mappings }) {
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [bulkAction, setBulkAction] = useState('');

  // Toggle row expansion
  const toggleRowExpansion = (rowIndex) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(rowIndex)) {
      newExpanded.delete(rowIndex);
    } else {
      newExpanded.add(rowIndex);
    }
    setExpandedRows(newExpanded);
  };

  // Toggle row selection
  const toggleRowSelection = (rowIndex) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(rowIndex)) {
      newSelected.delete(rowIndex);
    } else {
      newSelected.add(rowIndex);
    }
    setSelectedRows(newSelected);
  };

  // Select all rows
  const selectAll = () => {
    if (selectedRows.size === duplicates.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(duplicates.map((_, idx) => idx)));
    }
  };

  // Apply bulk action
  const applyBulkAction = () => {
    if (!bulkAction || selectedRows.size === 0) return;

    selectedRows.forEach(rowIndex => {
      if (bulkAction === 'create_new') {
        onResolutionChange(rowIndex, 'create_new');
      } else if (bulkAction === 'skip') {
        onResolutionChange(rowIndex, 'skip');
      } else if (bulkAction.startsWith('update_')) {
        // For update actions, use the first match as default
        const duplicate = duplicates.find(d => d.rowIndex === rowIndex);
        // Support both API formats: potentialMatches (new) and matches (legacy)
        const dupMatches = duplicate?.potentialMatches || duplicate?.matches || [];
        if (duplicate && dupMatches.length > 0) {
          onResolutionChange(rowIndex, 'update', dupMatches[0].entityId || dupMatches[0].id);
        }
      }
    });

    // Clear selection after applying
    setSelectedRows(new Set());
    setBulkAction('');
  };

  // Get row data from CSV
  const getRowData = (rowIndex) => {
    if (!csvData || !csvData.rows || !csvData.rows[rowIndex]) return {};

    const row = csvData.rows[rowIndex];
    const mappedData = {};

    Object.entries(mappings).forEach(([csvField, entityField]) => {
      if (row[csvField]) {
        mappedData[entityField] = row[csvField];
      }
    });

    return mappedData;
  };

  // Get display name for entity
  const getEntityDisplayName = (data) => {
    return data.name ||
           `${data.firstName || ''} ${data.lastName || ''}`.trim() ||
           data.email ||
           'Unnamed Entity';
  };

  // Get confidence color
  const getConfidenceColor = (confidence) => {
    if (confidence >= 90) return 'error';
    if (confidence >= 70) return 'warning';
    if (confidence >= 50) return 'info';
    return 'default';
  };

  // No duplicates found
  if (!duplicates || duplicates.length === 0) {
    return (
      <Box>
        <Alert severity="success" icon={<CheckIcon />}>
          <Typography variant="subtitle1">
            No duplicates detected!
          </Typography>
          <Typography variant="body2">
            All {csvData?.rows?.length || 0} entities appear to be unique and can be imported.
          </Typography>
        </Alert>
      </Box>
    );
  }

  // Calculate statistics
  const stats = {
    total: duplicates.length,
    highConfidence: duplicates.filter(d => d.confidence >= 90).length,
    mediumConfidence: duplicates.filter(d => d.confidence >= 70 && d.confidence < 90).length,
    lowConfidence: duplicates.filter(d => d.confidence < 70).length,
    resolved: Object.keys(resolutions).length
  };

  return (
    <Box>
      {/* Statistics */}
      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Duplicate Detection Results
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Chip
            icon={<DuplicateIcon />}
            label={`${stats.total} potential duplicates`}
            color="warning"
          />
          {stats.highConfidence > 0 && (
            <Chip
              label={`${stats.highConfidence} high confidence`}
              color="error"
              size="small"
            />
          )}
          {stats.mediumConfidence > 0 && (
            <Chip
              label={`${stats.mediumConfidence} medium confidence`}
              color="warning"
              size="small"
            />
          )}
          {stats.lowConfidence > 0 && (
            <Chip
              label={`${stats.lowConfidence} low confidence`}
              color="info"
              size="small"
            />
          )}
        </Box>

        <LinearProgress
          variant="determinate"
          value={(stats.resolved / stats.total) * 100}
          sx={{ height: 8, borderRadius: 4 }}
        />
        <Typography variant="caption" color="text.secondary">
          {stats.resolved} of {stats.total} duplicates resolved
        </Typography>
      </Paper>
      {/* Bulk Actions */}
      <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<SelectAllIcon />}
            onClick={selectAll}
            variant="outlined"
            size="small"
          >
            {selectedRows.size === duplicates.length ? 'Deselect All' : 'Select All'}
          </Button>

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <Select
              value={bulkAction}
              onChange={(e) => setBulkAction(e.target.value)}
              displayEmpty
              disabled={selectedRows.size === 0}
            >
              <MenuItem value="">
                <em>Bulk action...</em>
              </MenuItem>
              <MenuItem value="create_new">Create as new entities</MenuItem>
              <MenuItem value="update_first">Update existing (use first match)</MenuItem>
              <MenuItem value="skip">Skip these rows</MenuItem>
            </Select>
          </FormControl>

          <Button
            variant="contained"
            onClick={applyBulkAction}
            disabled={!bulkAction || selectedRows.size === 0}
            size="small"
          >
            Apply to {selectedRows.size} selected
          </Button>

          {selectedRows.size > 0 && (
            <Typography variant="caption" color="text.secondary">
              {selectedRows.size} row{selectedRows.size > 1 ? 's' : ''} selected
            </Typography>
          )}
        </Box>
      </Paper>
      {/* Duplicate List */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={selectedRows.size === duplicates.length && duplicates.length > 0}
                  indeterminate={selectedRows.size > 0 && selectedRows.size < duplicates.length}
                  onChange={selectAll}
                />
              </TableCell>
              <TableCell>Row</TableCell>
              <TableCell>Import Data</TableCell>
              <TableCell>Potential Matches</TableCell>
              <TableCell>Confidence</TableCell>
              <TableCell>Resolution</TableCell>
              <TableCell width={50}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* eslint-disable-next-line max-lines-per-function */}
            {duplicates.map((duplicate, index) => {
              const rowData = getRowData(duplicate.rowIndex);
              const displayName = getEntityDisplayName(rowData);
              const isExpanded = expandedRows.has(index);
              const isSelected = selectedRows.has(index);
              const resolution = resolutions[duplicate.rowIndex] || { action: 'create_new' };
              // Support both API formats: potentialMatches (new) and matches (legacy)
              const matches = duplicate.potentialMatches || duplicate.matches || [];
              // Get confidence from first match or top-level duplicate property
              const confidence = matches.length > 0
                ? Math.round((matches[0].confidence || 0) * 100)
                : (duplicate.confidence || 0);

              return (
                <React.Fragment key={index}>
                  <TableRow hover selected={isSelected}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isSelected}
                        onChange={() => toggleRowSelection(index)}
                      />
                    </TableCell>

                    <TableCell>
                      {duplicate.rowIndex + 1}
                    </TableCell>

                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon color="action" />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {displayName}
                          </Typography>
                          {rowData.email && (
                            <Typography variant="caption" color="text.secondary">
                              {rowData.email}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">
                        {matches.length} match{matches.length > 1 ? 'es' : ''} found
                      </Typography>
                      {matches.length > 0 && (
                        <>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {matches[0].displayName || matches[0].originalText || matches[0].entityId}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            via {matches[0].matchedField || 'unknown'}
                          </Typography>
                        </>
                      )}
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={`${confidence}%`}
                        color={getConfidenceColor(confidence)}
                        size="small"
                      />
                    </TableCell>

                    <TableCell>
                      <FormControl size="small" fullWidth>
                        <Select
                          value={resolution.action}
                          onChange={(e) => {
                            const action = e.target.value;
                            if (action === 'update' && matches.length > 0) {
                              // Use entityId from the API response
                              onResolutionChange(duplicate.rowIndex, action, matches[0].entityId || matches[0].id);
                            } else {
                              onResolutionChange(duplicate.rowIndex, action);
                            }
                          }}
                        >
                          <MenuItem value="create_new">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <AddIcon fontSize="small" />
                              Create New
                            </Box>
                          </MenuItem>
                          <MenuItem value="update">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <MergeIcon fontSize="small" />
                              Update Existing
                            </Box>
                          </MenuItem>
                          <MenuItem value="skip">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <SkipIcon fontSize="small" />
                              Skip Row
                            </Box>
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>

                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => toggleRowExpansion(index)}
                      >
                        {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={7} sx={{ py: 0, border: 0 }}>
                      <Collapse in={isExpanded}>
                        <Box sx={{ p: 2, backgroundColor: 'grey.50' }}>
                          <Grid container spacing={2}>
                            <Grid
                              size={{
                                xs: 12,
                                md: 6
                              }}>
                              <Card variant="outlined">
                                <CardContent>
                                  <Typography variant="subtitle2" gutterBottom>
                                    Import Data (Row {duplicate.rowIndex + 1})
                                  </Typography>
                                  <Divider sx={{ my: 1 }} />
                                  {Object.entries(rowData).map(([field, value]) => (
                                    <Box key={field} sx={{ mb: 0.5 }}>
                                      <Typography variant="caption" color="text.secondary">
                                        {field}:
                                      </Typography>
                                      <Typography variant="body2">
                                        {value || '—'}
                                      </Typography>
                                    </Box>
                                  ))}
                                </CardContent>
                              </Card>
                            </Grid>

                            <Grid
                              size={{
                                xs: 12,
                                md: 6
                              }}>
                              <Card variant="outlined">
                                <CardContent>
                                  <Typography variant="subtitle2" gutterBottom>
                                    Existing Matches in Token Pool
                                  </Typography>
                                  <Divider sx={{ my: 1 }} />
                                  {matches.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">
                                      No exact matches found
                                    </Typography>
                                  ) : (
                                    matches.slice(0, 3).map((match, idx) => {
                                      const matchId = match.entityId || match.id;
                                      const matchConfidence = Math.round((match.confidence || 0) * 100);
                                      const displayName = match.displayName || match.canonicalText || match.originalText || match.name;
                                      return (
                                        <Paper
                                          key={idx}
                                          variant="outlined"
                                          sx={{
                                            p: 1,
                                            mb: 1,
                                            backgroundColor:
                                              resolution.action === 'update' && resolution.targetEntityId === matchId
                                                ? 'action.selected'
                                                : 'background.paper'
                                          }}
                                        >
                                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Box>
                                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                {displayName || matchId}
                                              </Typography>
                                              <Typography variant="caption" color="text.secondary" display="block">
                                                Matched by: {match.matchedField || match.type || 'unknown'} | Confidence: {matchConfidence}%
                                              </Typography>
                                              {match.tokenType && (
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                  Type: {match.tokenType} {match.occurrences ? `| Seen ${match.occurrences} times` : ''}
                                                </Typography>
                                              )}
                                              {match.originalText && match.originalText !== displayName && (
                                                <Typography variant="caption" display="block" sx={{ fontStyle: 'italic' }}>
                                                  Original: {match.originalText}
                                                </Typography>
                                              )}
                                            </Box>
                                            {resolution.action === 'update' && (
                                              <Radio
                                                checked={resolution.targetEntityId === matchId}
                                                onChange={() => onResolutionChange(duplicate.rowIndex, 'update', matchId)}
                                                size="small"
                                              />
                                            )}
                                          </Box>
                                        </Paper>
                                      );
                                    })
                                  )}
                                  {matches.length > 3 && (
                                    <Typography variant="caption" color="text.secondary">
                                      ...and {matches.length - 3} more matches
                                    </Typography>
                                  )}
                                </CardContent>
                              </Card>
                            </Grid>
                          </Grid>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {/* Info Alert */}
      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Resolution Options:
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: 2 }}>
          <li><strong>Create New:</strong> Import as a new entity (may create duplicates)</li>
          <li><strong>Update Existing:</strong> Update the matched entity with new data</li>
          <li><strong>Skip Row:</strong> Don't import this row</li>
        </Box>
      </Alert>
    </Box>
  );
}

export default DuplicateResolver;